export type WebDeviceKind =
  | "video-device"
  | "network-device"
  | "printer"
  | "media-device"
  | "smart-home-device"
  | "unknown";

export type WebInspectionConfidence = "none" | "low" | "medium";

export type WebServiceInspection = {
  readable: boolean;
  kind: WebDeviceKind;
  label: string;
  confidence: WebInspectionConfidence;
  title?: string;
  generator?: string;
  contentType?: string;
  signals: string[];
};

export type WebHostClassification = {
  kind: WebDeviceKind;
  label: string;
  confidence: WebInspectionConfidence;
  readableServices: number;
  signals: string[];
};

type FingerprintInput = {
  body: string;
  contentType?: string;
  headers?: Readonly<Record<string, string>>;
};

type Signature = {
  kind: Exclude<WebDeviceKind, "unknown">;
  label: string;
  patterns: readonly { expression: RegExp; signal: string; weight: number }[];
};

const SIGNATURES: readonly Signature[] = [
  {
    kind: "video-device",
    label: "Équipement vidéo possible",
    patterns: [
      { expression: /hikvision/i, signal: "Hikvision", weight: 4 },
      { expression: /dahua/i, signal: "Dahua", weight: 4 },
      { expression: /reolink/i, signal: "Reolink", weight: 4 },
      { expression: /amcrest/i, signal: "Amcrest", weight: 4 },
      { expression: /axis communications/i, signal: "Axis Communications", weight: 4 },
      { expression: /network\s+(?:ip\s+)?camera/i, signal: "Network camera", weight: 4 },
      { expression: /\bip\s*camera\b|\bipcam\b/i, signal: "IP camera", weight: 4 },
      { expression: /video surveillance/i, signal: "Video surveillance", weight: 3 },
      { expression: /network video (?:recorder|transmitter)/i, signal: "Network video", weight: 3 },
      { expression: /\bonvif\b/i, signal: "ONVIF", weight: 3 },
      { expression: /\b(?:nvr|dvr)\b/i, signal: "NVR/DVR", weight: 2 },
    ],
  },
  {
    kind: "network-device",
    label: "Équipement réseau possible",
    patterns: [
      { expression: /openwrt/i, signal: "OpenWrt", weight: 4 },
      { expression: /routeros|mikrotik/i, signal: "RouterOS/MikroTik", weight: 4 },
      { expression: /ubiquiti|\bunifi\b/i, signal: "Ubiquiti/UniFi", weight: 4 },
      { expression: /fritz!?box/i, signal: "FRITZ!Box", weight: 4 },
      { expression: /asuswrt/i, signal: "ASUSWRT", weight: 4 },
      { expression: /tp-?link|netgear/i, signal: "Marque réseau", weight: 3 },
      { expression: /wireless router|access point|router login/i, signal: "Interface routeur", weight: 3 },
      { expression: /\b(?:livebox|freebox|bbox)\b/i, signal: "Box Internet", weight: 3 },
    ],
  },
  {
    kind: "printer",
    label: "Imprimante possible",
    patterns: [
      { expression: /hp embedded web server|\blaserjet\b/i, signal: "Interface HP", weight: 4 },
      { expression: /epson web config|brother web based management/i, signal: "Interface d’imprimante", weight: 4 },
      { expression: /canon (?:printer|imageclass)/i, signal: "Imprimante Canon", weight: 4 },
      { expression: /printer status|print server/i, signal: "Service d’impression", weight: 3 },
    ],
  },
  {
    kind: "media-device",
    label: "Équipement multimédia possible",
    patterns: [
      { expression: /chromecast|google tv/i, signal: "Google TV/Chromecast", weight: 4 },
      { expression: /\broku\b|apple tv/i, signal: "Lecteur multimédia", weight: 4 },
      { expression: /\bwebos\b|smart tv|samsung tv/i, signal: "Téléviseur connecté", weight: 4 },
    ],
  },
  {
    kind: "smart-home-device",
    label: "Équipement domotique possible",
    patterns: [
      { expression: /home assistant/i, signal: "Home Assistant", weight: 4 },
      { expression: /hue bridge|philips hue/i, signal: "Philips Hue", weight: 4 },
      { expression: /\bshelly\b|\bsonos\b/i, signal: "Équipement domotique", weight: 3 },
    ],
  },
];

const KIND_PRIORITY: Readonly<Record<WebDeviceKind, number>> = {
  unknown: 0,
  "smart-home-device": 1,
  "media-device": 2,
  printer: 3,
  "network-device": 4,
  "video-device": 5,
};

const CONFIDENCE_PRIORITY: Readonly<Record<WebInspectionConfidence, number>> = {
  none: 0,
  low: 1,
  medium: 2,
};

function decodeBasicEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#(?:39|x27);/gi, "'");
}

function cleanText(value: string, maximumLength = 120): string {
  return decodeBasicEntities(value.replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maximumLength);
}

function extractTitle(body: string): string | undefined {
  const match = body.match(/<title\b[^>]*>([\s\S]*?)<\/title>/i);
  const title = match ? cleanText(match[1]) : "";
  return title || undefined;
}

function extractGenerator(body: string): string | undefined {
  for (const match of body.matchAll(/<meta\b[^>]*>/gi)) {
    const attributes = new Map<string, string>();
    for (const attribute of match[0].matchAll(/([\w:-]+)\s*=\s*(["'])(.*?)\2/gi)) {
      attributes.set(attribute[1].toLowerCase(), attribute[3]);
    }
    if (attributes.get("name")?.toLowerCase() === "generator") {
      const generator = cleanText(attributes.get("content") ?? "");
      if (generator) return generator;
    }
  }
  return undefined;
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

export function unreadableWebInspection(): WebServiceInspection {
  return {
    readable: false,
    kind: "unknown",
    label: "Interface web observée — type indéterminé",
    confidence: "none",
    signals: [],
  };
}

export function classifyReadableWebDocument(input: FingerprintInput): WebServiceInspection {
  const title = extractTitle(input.body);
  const generator = extractGenerator(input.body);
  const headerValues = Object.values(input.headers ?? {});
  const searchable = [title, generator, input.contentType, ...headerValues, input.body]
    .filter((value): value is string => Boolean(value))
    .join("\n");

  const candidates = SIGNATURES.map((signature) => {
    const matches = signature.patterns.filter(({ expression }) => expression.test(searchable));
    return {
      signature,
      score: matches.reduce((total, match) => total + match.weight, 0),
      signals: matches.map((match) => `Indice textuel « ${match.signal} »`),
    };
  })
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => {
      const scoreDelta = right.score - left.score;
      if (scoreDelta !== 0) return scoreDelta;
      return KIND_PRIORITY[right.signature.kind] - KIND_PRIORITY[left.signature.kind];
    });

  const strongest = candidates[0];
  const descriptiveSignals = unique([
    ...(title ? [`Titre web : ${title}`] : []),
    ...(generator ? [`Générateur : ${generator}`] : []),
  ]);

  if (!strongest) {
    return {
      readable: true,
      kind: "unknown",
      label: "Interface web non identifiée",
      confidence: "none",
      ...(title ? { title } : {}),
      ...(generator ? { generator } : {}),
      ...(input.contentType ? { contentType: input.contentType } : {}),
      signals: descriptiveSignals,
    };
  }

  return {
    readable: true,
    kind: strongest.signature.kind,
    label: strongest.signature.label,
    confidence: strongest.score >= 4 ? "medium" : "low",
    ...(title ? { title } : {}),
    ...(generator ? { generator } : {}),
    ...(input.contentType ? { contentType: input.contentType } : {}),
    signals: unique([...strongest.signals, ...descriptiveSignals]).slice(0, 4),
  };
}

export function classifyWebHost(
  ip: string,
  inspections: readonly WebServiceInspection[],
): WebHostClassification {
  const readableServices = inspections.filter((inspection) => inspection.readable).length;
  const strongest = [...inspections]
    .filter((inspection) => inspection.kind !== "unknown")
    .sort((left, right) => {
      const confidenceDelta =
        CONFIDENCE_PRIORITY[right.confidence] - CONFIDENCE_PRIORITY[left.confidence];
      if (confidenceDelta !== 0) return confidenceDelta;
      return KIND_PRIORITY[right.kind] - KIND_PRIORITY[left.kind];
    })[0];

  if (strongest) {
    return {
      kind: strongest.kind,
      label: strongest.label,
      confidence: strongest.confidence,
      readableServices,
      signals: unique(
        inspections
          .filter((inspection) => inspection.kind === strongest.kind)
          .flatMap((inspection) => inspection.signals),
      ).slice(0, 4),
    };
  }

  const lastOctet = Number(ip.split(".").at(-1));
  if (lastOctet === 1 || lastOctet === 254) {
    return {
      kind: "network-device",
      label: "Passerelle ou routeur possible",
      confidence: "low",
      readableServices,
      signals: ["Adresse souvent utilisée par une passerelle réseau"],
    };
  }

  return {
    kind: "unknown",
    label:
      readableServices > 0
        ? "Interface web non identifiée"
        : "Interface web observée — type indéterminé",
    confidence: "none",
    readableServices,
    signals:
      readableServices > 0
        ? unique(inspections.flatMap((inspection) => inspection.signals)).slice(0, 4)
        : ["Réponse opaque : Chrome masque son contenu au code de la page (CORS)"],
  };
}
